// Custom hook: all handler functions and business logic for ProductionOrder
import { useState } from "react";
import { message, Modal } from "antd";
import Axios from "axios";
import { getOtherCharges, getBomProducts, getChooseProduct, getSearchedProducts, makeToOrder, makeToStock } from "../../services/generic";
import { upsertBOMProduction, unlinkTill } from "../../services";
import messages from "../../errors/messages.en-IN.json";
import { roundTo } from "../../utils/tillHelpers";
import db from "../../database";
import { toFixedTruncate, getExpiryDate, modifiedWithProperPrecision } from "./productionUtils";
import {
  savingTotalSalePrice,
  savingUpdateCartTotals,
  savingSearchedProductsData,
  savingIsStockModal,
  savingDisableFlag,
  savingSelectedProduct,
  savingFormattedDate,
  savingSelectedProductSalePrice,
  savingPricingMethod,
  savingOtherChargesData,
  savingRemarks,
  savingQuantityInput,
  savingTareWeight,
  savingTotalWeight,
  savingExpiryDate,
  savingCopies,
  removeLocalStorageData,
} from "./productionStorage";
import NotFoundImg from "../../assets/images/NotFoundImg.svg";
import useSessionStore from "../../stores/useSessionStore";
import { trace } from "../../utils/debugTrace";
import { buildLockedSignInUrl } from "../../constants/oAuthValidation";

const useProductionHandlers = ({
  tillData,
  posConfig,
  csBunitId,
  pricePrecision,
  printerURL,
  domainURL,
  redirectUrl,
  username,
  email,
  // State values
  searchedProductsData,
  setSearchedProductsData,
  quantityInputValue,
  setQuantityInputValue,
  keyBoardInputValue,
  setKeyBoardInputValue,
  tempQuantityInputValue,
  setTempQuantityInputValue,
  selectedProduct,
  setSelectedProduct,
  selectedProductSalePrice,
  setSelectedProductSalePrice,
  chooseProductsData,
  setChooseProductsData,
  chooseSearchedProduct,
  formattedDate,
  setFormattedDate,
  searchedProduct,
  setSearchedProduct,
  setChooseSearchedProduct,
  setSearchedProductsFlag,
  setSearchedProductsDrawerData,
  subtotal,
  setSubtotal,
  totalItems,
  setTotalItems,
  totalQty,
  setTotalQty,
  totalSalePrice,
  setTotalSalePrice,
  otherChargesData,
  setOtherChargesData,
  tempOtherChargesData,
  setTempOtherChargesData,
  pricingMethod,
  setPricingMethod,
  reverseOrderData,
  setReverseOrderData,
  isStockModal,
  setIsStockModal,
  isVisible,
  setIsVisible,
  stockValidationData,
  setStockValidationData,
  disableSearch,
  setDisableSearch,
  selectedRowKey,
  setSelectedRowKey,
  showInputFields,
  setShowInputFields,
  QtyShowInputFields,
  setQtyShowInputFields,
  selectedQuantity,
  setSelectedQuantity,
  selectedTotalQuantity,
  setSelectedTotalQuantity,
  selectedKey,
  setSelectedKey,
  isNewInput,
  setIsNewInput,
  isFirstInput,
  setIsFirstInput,
  editingKey,
  setEditingKey,
  editingValue,
  setEditingValue,
  selectedInputType,
  setSelectedInputType,
  originalValue,
  setOriginalValue,
  totalWeight,
  setTotalWeight,
  tareWeight,
  setTareWeight,
  totalWeights,
  setTotalWeights,
  expiryDate,
  setExpiryDate,
  copies,
  setCopies,
  isWeightPopupVisible,
  setIsWeightPopupVisible,
  tempSelectProduct,
  setTempSelectProduct,
  setChooseProduct,
  chooseProduct,
  setLoading,
  setChooseAProductLoading,
  setMtsModalVisible,
  setMtoModalVisible,
  searchTextStock,
  setSearchTextStock,
  searchTextOrder,
  setSearchTextOrder,
  remarks,
  setRemarks,
  showRemarks,
  setShowRemarks,
  otherCharges,
  setOtherCharges,
  layoutName,
  setLayoutName,
  tempRemarks,
  setTempRemarks,
  tillLink,
  setTillLink,
  isLocked,
  setIsLocked,
  showOrderHistory,
  setShowOrderHistory,
  // Refs
  inputSearchRef,
  plusButtonRef,
  pricingMethodRef,
  qtyUnitInputRef,
  // Misc
  hasReverseOrderData,
}) => {
  // ── modifiedData (bound to pricePrecision) ──
  const modifiedData = (data) => modifiedWithProperPrecision(data, pricePrecision);

  // ── Cart helpers ──
  const updateCartTotals = (updatedItems) => {
    if (!Array.isArray(updatedItems)) updatedItems = [];
    const totalItemsCount = updatedItems.length;
    const totalQtyCount = updatedItems.reduce((acc, product) => acc + (product.totalWeight || 0), 0);
    const subtotalAmount = updatedItems.reduce((acc, product) => acc + (product.total || 0), 0);
    setTotalItems(totalItemsCount);
    setTotalQty(totalQtyCount);
    setSubtotal(subtotalAmount);
    savingUpdateCartTotals(totalItemsCount, totalQtyCount, subtotalAmount);
  };

  const checkStockData = (products) => {
    const productsWithInsufficientStock = products?.filter((bomProduct) => {
      const quantity = bomProduct?.totalWeight;
      const stock = bomProduct?.stock !== undefined ? bomProduct.stock : bomProduct?.stockOnHand;
      return quantity > stock;
    });
    setStockValidationData(productsWithInsufficientStock);
    return productsWithInsufficientStock?.length > 0 || false;
  };

  const updateQuantityAndProducts = (newQuantity) => {
    const modifiedProductData = searchedProductsData?.map((product) => ({
      ...product,
      totalWeight: product.weight * newQuantity,
      total: product.weight * newQuantity * product.sale_price,
    }));
    const stockFlag = checkStockData(modifiedProductData);
    if (!stockFlag) {
      addOrUpdateProducts(modifiedProductData);
      setQuantityInputValue(newQuantity);
      setKeyBoardInputValue(newQuantity);
      setTempQuantityInputValue(newQuantity);
      savingQuantityInput(newQuantity);
    } else {
      setIsVisible(true);
      setKeyBoardInputValue(quantityInputValue);
      setTempQuantityInputValue(quantityInputValue);
    }
  };

  // ── UOM data helpers ──
  const updateProductWithUOMData = async (product) => {
    let uomData = [];
    if (product.cs_uom_id) {
      await db.productUom
        .where("csUomId")
        .equals(product.cs_uom_id)
        .toArray()
        .then((uom) => {
          if (uom.length > 0) uomData = uom;
        });
    }
    return {
      ...product,
      ediCode: uomData[0]?.ediCode,
      uomData,
      total: product.totalWeight * product.sale_price,
      weight: parseFloat(product.weight),
      totalWeight: parseFloat(product.totalWeight),
    };
  };

  const updateChooseProductWithUOMData = async (product) => {
    let uomData = [];
    if (product.cs_uom_id) {
      await db.productUom
        .where("csUomId")
        .equals(product.cs_uom_id)
        .toArray()
        .then((uom) => {
          if (uom.length > 0) uomData = uom;
        });
    }
    return { ...product, ediCode: uomData[0]?.ediCode, uomData, qty: parseFloat(product.qty) };
  };

  // ── addOrUpdateProducts ──
  const addOrUpdateProducts = async (products) => {
    const modifiedArr = await Promise.all(products?.map((product) => updateProductWithUOMData(product)));
    const stockFlag = checkStockData(modifiedArr);
    if (stockFlag) {
      savingIsStockModal(true);
      setIsStockModal(true);
    }
    let finalData = modifiedData(modifiedArr);
    updateCartTotals(finalData);
    setSearchedProductsData(finalData);
    savingSearchedProductsData(finalData);
  };

  // ── handleInputSubmit / handleEditStart ──
  const handleInputSubmit = async () => {
    const updatedItems = searchedProductsData?.map((item) => {
      if (item.key === editingKey) {
        if (selectedInputType === "weight") {
          const newWeight = parseFloat(editingValue || item.weight);
          return { ...item, weight: newWeight, totalWeight: newWeight * quantityInputValue, total: newWeight * quantityInputValue * item.sale_price };
        } else if (selectedInputType === "totalWeight") {
          const newTotalWeight = parseFloat(editingValue || item.totalWeight);
          return { ...item, weight: newTotalWeight / (quantityInputValue || 1), totalWeight: newTotalWeight, total: newTotalWeight * item.sale_price };
        }
      }
      return item;
    });

    const editedItem = updatedItems.find((item) => item.key === editingKey);
    if (editedItem) {
      const stockFlag = checkStockData([editedItem]);
      if (stockFlag) {
        setIsVisible(true);
        setEditingValue(originalValue);
        return;
      }
    }

    let finalData = modifiedData(updatedItems);
    await addOrUpdateProducts(finalData);
    setSearchedProductsData(finalData);
    setSelectedInputType("");
    setEditingKey(null);
    setEditingValue("");
  };

  const handleEditStart = (record, inputType) => {
    const originalFormattedValue = toFixedTruncate(record[inputType], record.uomData[0]?.stdprecision);
    setOriginalValue(originalFormattedValue);
    setEditingKey(record.key);
    setSelectedInputType(inputType);
  };

  // ── Keypad amount handlers ──
  const handleAmount = (value) => {
    const amountInput = document.getElementById("sm-amount-input");
    if (amountInput) amountInput?.focus();
    const index = searchedProductsData?.findIndex((item) => item.key === selectedKey);
    if (value === "x") {
      setSelectedQuantity((prev) => {
        const nv = prev.toString().slice(0, -1);
        return nv === "" ? "" : nv;
      });
    } else if (value === ".") {
      setSelectedQuantity((prev) =>
        searchedProductsData[index] && Array.isArray(searchedProductsData[index]?.uomData) && searchedProductsData[index]?.uomData[0]?.decimal === "Y" ? prev + "." : prev
      );
    } else {
      if (isNewInput) {
        setSelectedQuantity(parseInt(value));
        setIsNewInput(false);
      } else {
        setSelectedQuantity((prev) => {
          const isDecimalAllowed = searchedProductsData[index] && Array.isArray(searchedProductsData[index]?.uomData) && searchedProductsData[index]?.uomData[0]?.decimal === "Y";
          if (isDecimalAllowed) return prev + value;
          const updatedQuantity = prev + value;
          return updatedQuantity.toString().length <= 4 ? updatedQuantity : prev;
        });
      }
    }
  };

  const handleTotalAmount = (value) => {
    const amountInput = document.getElementById("sm-total-amount-input");
    if (amountInput) amountInput?.focus();
    const index = searchedProductsData?.findIndex((item) => item.key === selectedKey);
    if (value === "x") {
      setSelectedTotalQuantity((prev) => {
        const nv = prev.toString().slice(0, -1);
        return nv === "" ? "" : nv;
      });
    } else if (value === ".") {
      setSelectedTotalQuantity((prev) =>
        searchedProductsData[index] && Array.isArray(searchedProductsData[index]?.uomData) && searchedProductsData[index]?.uomData[0]?.decimal === "Y" ? prev + "." : prev
      );
    } else {
      if (isNewInput) {
        setSelectedTotalQuantity(parseInt(value));
        setIsNewInput(false);
      } else {
        setSelectedTotalQuantity((prev) => {
          const isDecimalAllowed = searchedProductsData[index] && Array.isArray(searchedProductsData[index]?.uomData) && searchedProductsData[index]?.uomData[0]?.decimal === "Y";
          if (isDecimalAllowed) return prev + value;
          const updatedQuantity = prev + value;
          return updatedQuantity.toString().length <= 4 ? updatedQuantity : prev;
        });
      }
    }
  };

  const handleAmountQty = (value) => {
    if (value === "x") {
      setKeyBoardInputValue((prev) => {
        const nv = prev.toString().slice(0, -1);
        return nv === "" ? "" : nv;
      });
    } else if (value === ".") {
      setKeyBoardInputValue((prev) =>
        selectedProduct && Array.isArray(selectedProduct.uomData) && selectedProduct.uomData[0] && selectedProduct.uomData[0].decimal === "Y" ? prev + "." : prev
      );
    } else {
      if (isFirstInput) {
        setKeyBoardInputValue(parseInt(value));
        setIsFirstInput(false);
      } else {
        setKeyBoardInputValue((prev) => {
          const isDecimalAllowed = selectedProduct && Array.isArray(selectedProduct.uomData) && selectedProduct.uomData[0] && selectedProduct.uomData[0].decimal === "Y";
          if (isDecimalAllowed) return prev + value;
          const updatedQuantity = prev + value;
          return updatedQuantity.toString().length <= 4 ? updatedQuantity : prev;
        });
      }
    }
    const amountInput = document.getElementById("sm-qty-input");
    if (amountInput) amountInput?.focus();
  };

  const handleEnter = async () => {
    if (selectedKey !== null) {
      let updatedItems = searchedProductsData?.map((item) => {
        if (item.key === selectedKey) {
          return {
            ...item,
            weight: selectedQuantity === "" ? item.weight : parseFloat(selectedQuantity),
            totalWeight: selectedQuantity === "" ? item.totalWeight : parseFloat(selectedQuantity) * (quantityInputValue === "" ? 1 : quantityInputValue),
            total: selectedQuantity === "" ? item.total : parseFloat(selectedQuantity) * (quantityInputValue === "" ? 1 : quantityInputValue) * item.sale_price,
          };
        }
        return item;
      });
      const stockFlag = checkStockData([updatedItems[selectedKey]]);
      if (stockFlag) {
        setIsVisible(true);
      } else {
        await addOrUpdateProducts(updatedItems);
        setIsNewInput(true);
        setSelectedRowKey(null);
        setShowInputFields(false);
        setQtyShowInputFields(false);
        setSelectedRowKey(null);
      }
    }
  };

  const handleTotalEnter = async () => {
    if (selectedKey !== null) {
      let updatedItems = searchedProductsData?.map((item) =>
        item.key === selectedKey
          ? {
              ...item,
              weight: selectedTotalQuantity === "" ? item.weight : parseFloat(selectedTotalQuantity) / quantityInputValue,
              totalWeight: selectedTotalQuantity === "" ? item.totalWeight : parseFloat(selectedTotalQuantity),
              total: selectedTotalQuantity === "" ? item.total : parseFloat(selectedTotalQuantity) * item.sale_price,
            }
          : item
      );
      const stockFlag = checkStockData([updatedItems[selectedKey]]);
      if (stockFlag) {
        setIsVisible(true);
      } else {
        await addOrUpdateProducts(updatedItems);
        setIsNewInput(true);
        setSelectedRowKey(null);
        setShowInputFields(false);
        setQtyShowInputFields(false);
        setSelectedRowKey(null);
      }
    }
  };

  const handleEnterQty = () => {
    let modifiedProductData = searchedProductsData?.map((product) => ({
      ...product,
      totalWeight: product.weight * (keyBoardInputValue === "" ? 1 : keyBoardInputValue),
      total: product.weight * (keyBoardInputValue === "" ? 1 : keyBoardInputValue) * product.sale_price,
    }));
    const stockFlag = checkStockData(modifiedProductData);
    if (stockFlag) {
      setIsVisible(true);
      setKeyBoardInputValue(quantityInputValue);
      setTempQuantityInputValue(quantityInputValue);
    } else {
      addOrUpdateProducts(modifiedProductData);
      setQuantityInputValue(keyBoardInputValue === "" ? 1 : keyBoardInputValue);
      setTempQuantityInputValue(keyBoardInputValue === "" ? 1 : keyBoardInputValue);
      savingQuantityInput(keyBoardInputValue === "" ? 1 : keyBoardInputValue);
      setQtyShowInputFields(false);
    }
  };

  const incrementQuantity = () => setSelectedQuantity((prev) => parseInt(prev) + 1);
  const decrementQuantity = () =>
    setSelectedQuantity((prev) => {
      const nv = parseInt(prev) - 1;
      return nv >= 0 ? nv : 0;
    });
  const incrementTotalQuantity = () => setSelectedTotalQuantity((prev) => parseInt(prev) + 1);
  const decrementTotalQuantity = () =>
    setSelectedTotalQuantity((prev) => {
      const nv = parseInt(prev) - 1;
      return nv >= 0 ? nv : 0;
    });
  const incrementQuantity1 = () => setKeyBoardInputValue((prev) => parseInt(prev) + 1);
  const decrementQuantity1 = () =>
    setKeyBoardInputValue((prev) => {
      const nv = parseInt(prev) - 1;
      return nv >= 1 ? nv : 1;
    });

  // ── BOM lines ──
  const returnBOMProducts = async (product) => {
    const params = { productId: product.m_product_id, bunitId: csBunitId, tillId: tillData?.tillAccess?.cwrTill?.cwrTillID };
    const stringifiedFields = JSON.stringify(params);
    const updatedStrings = stringifiedFields.replace(/\\"/g, '\\"');
    const stringRequest = JSON.stringify(updatedStrings);
    const bomProductsQuery = { query: `mutation { executeAPIBuilder(apiBuilderId: "67b85e97f34cb07cda3d57cc", params: ${stringRequest})}` };
    const bomProductsResponse = await getBomProducts(bomProductsQuery);
    let productData = JSON.parse(bomProductsResponse.data.data.executeAPIBuilder);
    return productData?.map((product, index) => ({
      ...product,
      isBom: "Y",
      key: index,
      uomname: product.uom,
      product_name: product.name,
      product_value: product.value,
      weight: product.qty,
      totalWeight: product.qty,
      stock: product.qtyonhand !== null ? product.qtyonhand : 0,
      sale_price: product.sale_price !== null ? product.sale_price : 0,
      total: product.qty * product.sale_price || 0,
    }));
  };

  const addBOMLines = async (product) => {
    // BUG-1943 diagnostics: manufacturing items sometimes fail to auto-add
    // consumption/BOM lines. Capture what addBOMLines actually saw so a real
    // occurrence can be confirmed before changing this logic further.
    trace("ProductionOrder", "addBOMLines start", {
      mProductId: product?.m_product_id,
      productValue: product?.value,
      showTareWeight: posConfig?.showTareWeight,
      hasBomproducts: Array.isArray(product?.bomproducts),
      bomproductsCount: product?.bomproducts?.length ?? null,
    });
    let modifiedArr =
      posConfig?.showTareWeight === "Y"
        ? await returnBOMProducts(product)
        : product?.bomproducts
          ? product?.bomproducts?.map((bom, index) => ({
              isBom: "Y",
              key: index,
              cs_uom_id: bom.bom_uom,
              uomname: bom.bom_uomname,
              product_name: bom.bomproductname,
              product_value: bom?.bomproductvalue,
              m_product_id: bom.m_bomproduct_id,
              weight: bom.qty,
              totalWeight: bom.qty,
              stock: bom.stock !== null ? bom.stock : 0,
              sale_price: bom.sale_price,
              total: bom.qty * bom.sale_price,
            }))
          : [];

    if (posConfig?.showTareWeight === "Y") {
      const finalValue = product.requiredQuantity === "" ? 0 : parseFloat(product.requiredQuantity - (product?.tareweight || 0));
      const modifiedProductData = modifiedArr?.map((product) => ({
        ...product,
        totalWeight: product.weight * finalValue,
        total: product.weight * finalValue * product.sale_price,
      }));
      await addOrUpdateProducts(modifiedProductData);
      setQuantityInputValue(finalValue);
      setKeyBoardInputValue(finalValue);
      setTempQuantityInputValue(finalValue);
      savingQuantityInput(finalValue);
    } else {
      await addOrUpdateProducts(modifiedArr);
    }

    trace("ProductionOrder", "addBOMLines done", { linesAdded: modifiedArr?.length ?? 0 });

    if (modifiedArr?.length > 0) {
      savingDisableFlag(true);
      setDisableSearch(true);
    } else {
      setDisableSearch(false);
    }
  };

  // ── saveDateAndSalePrice ──
  const saveDateAndSalePrice = (product) => {
    const currentDate = new Date();
    const fDate = currentDate.getDate().toString().padStart(2, "0") + "/" + (currentDate.getMonth() + 1).toString().padStart(2, "0") + "/" + currentDate.getFullYear();
    setFormattedDate(fDate);
    savingFormattedDate(fDate);
    setSelectedProductSalePrice(product?.sale_price !== null ? product?.sale_price : 0);
    savingSelectedProductSalePrice(product?.sale_price !== null ? product?.sale_price : 0);
  };

  // ── Product selection ──
  const handleProductSelect = (product) => async () => {
    saveDateAndSalePrice(product);
    if (product?.productmanufacturing?.[0]?.standard_price === "Y") {
      savingPricingMethod("Standard Price");
      setPricingMethod("Standard Price");
    }
    let uomData = [];
    if (product.cs_uom_id) {
      await db.productUom
        .where("csUomId")
        .equals(product.cs_uom_id)
        .toArray()
        .then((uom) => {
          if (uom.length > 0) uomData = uom;
        });
    }
    product.uomData = uomData;
    product.ediCode = uomData[0]?.ediCode;
    const currentDate = new Date();
    const today = currentDate.getDate().toString().padStart(2, "0") + "/" + (currentDate.getMonth() + 1).toString().padStart(2, "0") + "/" + currentDate.getFullYear();
    const date = getExpiryDate(today, product?.shelf_life || 0);
    setExpiryDate(date);
    savingExpiryDate(date);
    await addBOMLines(product);
    setTareWeight(product?.tareweight || 0);
    savingTareWeight(product?.tareweight || 0);
    setSelectedProduct(product);
    savingSelectedProduct(product);
    setChooseProduct(false);
  };

  const handleChooseProductSelect = async (product) => {
    saveDateAndSalePrice(product);
    let uomData = [];
    if (product.cs_uom_id) {
      await db.productUom
        .where("csUomId")
        .equals(product.cs_uom_id)
        .toArray()
        .then((uom) => {
          if (uom.length > 0) uomData = uom;
        });
    }
    product.uomData = uomData;
    product.ediCode = uomData[0]?.ediCode;
    await addBOMLines(product);
    savingSelectedProduct(product);
    const currentDate = new Date();
    const today = currentDate.getDate().toString().padStart(2, "0") + "/" + (currentDate.getMonth() + 1).toString().padStart(2, "0") + "/" + currentDate.getFullYear();
    const date = getExpiryDate(today, product?.shelf_life || 0);
    setExpiryDate(date);
    savingExpiryDate(date);
    setTareWeight(product?.tareweight || 0);
    savingTareWeight(product?.tareweight || 0);
    setSearchTextStock("");
    setChooseProductsData([]);
    setMtsModalVisible(false);
    setMtoModalVisible(false);
    setSelectedProduct(product);
  };

  // ── Choose product / pagination ──
  const handleChooseProduct = async (key) => {
    setChooseAProductLoading(true);
    if (posConfig?.showTareWeight === "Y" && key === "stock") setMtsModalVisible(true);
    else if (posConfig?.showTareWeight === "Y" && key === "order") setMtoModalVisible(true);
    setLoading(true);
    const params = { limit: "50", offset: "0", searchKey: chooseSearchedProduct || "", cs_bunit_id: csBunitId };
    const paramsForStock = { searchKey: "", bunitId: csBunitId, limit: "50", offset: "0" };
    const paramsForOrder = { searchKey: "", bunitId: csBunitId, tillId: tillData?.tillAccess?.cwrTill?.cwrTillID, limit: "50", offset: "0" };
    const builderConfig =
      posConfig?.showTareWeight === "Y"
        ? key === "stock"
          ? { id: "67b8539ef34cb07cda3d57b4", paramData: paramsForStock }
          : { id: "67b83968f34cb07cda3d57b0", paramData: paramsForOrder }
        : { id: "66f150288e3f1f618d89f45e", paramData: params };
    const chooseProductQuery = {
      query: `mutation ExecuteAPIBuilder($apiBuilderId: String, $params: String) { executeAPIBuilder(apiBuilderId: $apiBuilderId, params: $params)}`,
      variables: { apiBuilderId: builderConfig.id, params: JSON.stringify(builderConfig.paramData) },
    };
    const chooseProductResponse = await getChooseProduct(chooseProductQuery);
    if (chooseProductResponse.status === 200) {
      let chooseProductData = JSON.parse(chooseProductResponse?.data?.data?.executeAPIBuilder);
      let modifiedArr = await Promise.all(chooseProductData?.map((product) => updateChooseProductWithUOMData(product)));
      setChooseProductsData(modifiedArr);
      if (posConfig?.showTareWeight !== "Y" || (key !== "stock" && key !== "order")) {
        setChooseProduct(true);
      }
    }
    setLoading(false);
    setChooseAProductLoading(false);
  };

  const handleScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
    if (scrollTop + clientHeight >= scrollHeight - 1) handleReachBottom();
  };

  const handleReachBottom = async () => {
    const params = { limit: "50", offset: chooseProductsData?.length.toString(), searchKey: chooseSearchedProduct || "", cs_bunit_id: csBunitId };
    const paramsForStock = { searchKey: "", bunitId: csBunitId };
    const reachBottomConfig =
      posConfig?.showTareWeight === "Y" ? { id: "67b8539ef34cb07cda3d57b4", paramData: paramsForStock } : { id: "66f150288e3f1f618d89f45e", paramData: params };
    const chooseProductQuery = {
      query: `mutation ExecuteAPIBuilder($apiBuilderId: String, $params: String) { executeAPIBuilder(apiBuilderId: $apiBuilderId, params: $params)}`,
      variables: { apiBuilderId: reachBottomConfig.id, params: JSON.stringify(reachBottomConfig.paramData) },
    };
    const chooseProductResponse = await getChooseProduct(chooseProductQuery);
    if (chooseProductResponse.status === 200) {
      const chooseProductData = JSON.parse(chooseProductResponse?.data?.data?.executeAPIBuilder);
      let modifiedArr = await Promise.all(chooseProductData?.map((product) => updateChooseProductWithUOMData(product)));
      setChooseProductsData((prevData) => [...prevData, ...modifiedArr]);
    }
  };

  // ── Stock / Order scroll + search ──
  const fetchMoreStockData = async () => {
    try {
      const params = { searchKey: `${searchTextStock}`, bunitId: csBunitId, limit: "50", offset: `${chooseProductsData.length}` };
      const paramsString = JSON.stringify(params).replace(/"/g, '\\"');
      const makeToStockQuery = { query: `mutation { executeAPIBuilder ( apiBuilderId : "67b8539ef34cb07cda3d57b4", params: "${paramsString}" )}` };
      const makeToStockResponse = await makeToStock(makeToStockQuery);
      if (makeToStockResponse.status === 200 && makeToStockResponse.data?.data?.executeAPIBuilder) {
        let makeToStockResponseData = JSON.parse(makeToStockResponse.data.data.executeAPIBuilder);
        let modifiedArr = await Promise.all(makeToStockResponseData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData([...chooseProductsData, ...modifiedArr]);
      } else {
        console.error("API response error:", makeToStockResponse.data);
        message.error(`${messages.info.retrieve_stock_failed}`);
      }
    } catch (error) {
      console.error("Error in handleMakeToStock:", error.message || error);
      message.error(`${messages.errors.orders.retrieve_stock_error}: ${error.message || error}`);
    }
  };

  const handleStockScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
    if (scrollTop + clientHeight >= scrollHeight - 1) fetchMoreStockData();
  };

  const handleSearchedStock = async () => {
    try {
      const params = { searchKey: `${searchTextStock}`, bunitId: csBunitId, limit: "50", offset: "0" };
      const paramsString = JSON.stringify(params).replace(/"/g, '\\"');
      const makeToStockQuery = { query: `mutation { executeAPIBuilder ( apiBuilderId : "67b8539ef34cb07cda3d57b4", params: "${paramsString}" )}` };
      const makeToStockResponse = await makeToStock(makeToStockQuery);
      if (makeToStockResponse.status === 200 && makeToStockResponse.data?.data?.executeAPIBuilder) {
        let makeToStockResponseData = JSON.parse(makeToStockResponse.data.data.executeAPIBuilder);
        let modifiedArr = await Promise.all(makeToStockResponseData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData([...modifiedArr]);
      } else {
        console.error("API response error:", makeToStockResponse.data);
        message.error(`${messages.info.retrieve_stock_failed}`);
      }
    } catch (error) {
      console.error("Error in handleMakeToStock:", error.message || error);
      message.error(`${messages.errors.orders.retrieve_stock_error}: ${error.message || error}`);
    }
  };

  const fetchMoreOrderData = async () => {
    try {
      const params = { searchKey: `${searchTextOrder}`, bunitId: csBunitId, tillId: tillData?.tillAccess?.cwrTill?.cwrTillID, limit: "50", offset: `${chooseProductsData.length}` };
      const paramsString = JSON.stringify(params).replace(/"/g, '\\"');
      const makeToOrderQuery = { query: `mutation { executeAPIBuilder ( apiBuilderId : "67b83968f34cb07cda3d57b0", params: "${paramsString}" )}` };
      const makeToOrderResponse = await makeToOrder(makeToOrderQuery);
      if (makeToOrderResponse.status === 200 && makeToOrderResponse.data?.data?.executeAPIBuilder) {
        let makeToOrderResponseData = JSON.parse(makeToOrderResponse.data.data.executeAPIBuilder);
        let modifiedArr = await Promise.all(makeToOrderResponseData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData([...chooseProductsData, ...modifiedArr]);
      } else {
        console.error("API response error:", makeToOrderResponse.data);
        message.error(`${messages.info.retrieve_order_failed}`);
      }
    } catch (error) {
      console.error("Error in handleMakeToOrder:", error.message || error);
      message.error(`${messages.errors.orders.retrive_order_error}: ${error.message || error}`);
    }
  };

  const handleOrderScroll = ({ scrollTop, scrollHeight, clientHeight }) => {
    if (scrollTop + clientHeight >= scrollHeight - 1) fetchMoreOrderData();
  };

  const handleSearchedOrder = async () => {
    try {
      const params = { searchKey: `${searchTextOrder}`, bunitId: csBunitId, tillId: tillData?.tillAccess?.cwrTill?.cwrTillID, limit: "50", offset: "0" };
      const paramsString = JSON.stringify(params).replace(/"/g, '\\"');
      const makeToOrderQuery = { query: `mutation { executeAPIBuilder ( apiBuilderId : "67b83968f34cb07cda3d57b0", params: "${paramsString}" )}` };
      const makeToOrderResponse = await makeToOrder(makeToOrderQuery);
      if (makeToOrderResponse.status === 200 && makeToOrderResponse.data?.data?.executeAPIBuilder) {
        let makeToOrderResponseData = JSON.parse(makeToOrderResponse.data.data.executeAPIBuilder);
        let modifiedArr = await Promise.all(makeToOrderResponseData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData([...modifiedArr]);
      } else {
        console.error("API response error:", makeToOrderResponse.data);
        message.error(`${messages.info.retrieve_order_failed}`);
      }
    } catch (error) {
      console.error("Error in handleMakeToOrder:", error.message || error);
      message.error(`${messages.errors.orders.retrive_order_error}: ${error.message || error}`);
    }
  };

  const updateChooseProductsData = (productId, value) => {
    setChooseProductsData((prevData) => {
      const data = [...prevData];
      const index = data.findIndex((item) => item.m_product_id === productId);
      if (index === -1) return prevData;
      let sanitizedValue = value;
      const isDecimalAllowed = data[index]?.uomData?.[0]?.decimal === "Y";
      const stdPrecision = data[index]?.uomData?.[0]?.stdprecision || 3;
      sanitizedValue = isDecimalAllowed ? sanitizedValue.replace(/[^0-9.]/g, "") : sanitizedValue.replace(/[^0-9]/g, "");
      if (isDecimalAllowed) {
        const dotCount = (sanitizedValue.match(/\./g) || []).length;
        if (dotCount > 1) sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf("."));
        if (sanitizedValue.startsWith(".")) sanitizedValue = "0" + sanitizedValue;
      }
      if (isDecimalAllowed && sanitizedValue.includes(".")) {
        const [integerPart, decimalPart] = sanitizedValue.split(".");
        sanitizedValue = integerPart + "." + decimalPart.slice(0, stdPrecision);
      }
      const parsedValue =
        sanitizedValue === "" || sanitizedValue === "." ? "" : isDecimalAllowed && sanitizedValue.endsWith(".") ? sanitizedValue : parseFloat(sanitizedValue) || 0;
      data[index] = { ...data[index], requiredQuantity: parsedValue };
      setTotalWeight(parseFloat(value));
      savingTotalWeight(parseFloat(value));
      return data;
    });
  };

  // ── notFoundModal ──
  const notFoundModal = () => {
    Modal.info({
      icon: null,
      okButtonProps: { style: { display: "none" } },
      content: (
        <div style={{ fontSize: "1vw", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingLeft: 0 }}>
          <img src={NotFoundImg} alt="Not Found" style={{ width: "120px", height: "100px", marginBottom: "5px", marginTop: "10px" }} />
          <p style={{ margin: "5px 0", fontSize: "2em", fontWeight: "600", color: "#0F0718" }}>Product Not Found</p>
          <hr style={{ width: "70%", color: "rgba(146,144,152,0.5" }} />
          <p style={{ color: "#0F0718", fontWeight: 400, fontSize: "1.25em", padding: "15px 15px 0 15px" }}>
            The scanned product is not in the system. Please check the barcode or enter the item manually.
          </p>
        </div>
      ),
      style: { textAlign: "center", height: "50vh", width: "50%" },
      bodyStyle: { padding: "0" },
      closable: false,
      onOk: null,
    });
    setTimeout(() => Modal.destroyAll(), 1500);
  };

  // ── Barcode / search handlers ──
  const fetchSearchedProductData = async (data, bunitId) => {
    let params = { searchKey: data, cs_bunit_id: bunitId };
    let bomQty;
    if (tillData?.posScanConfigs?.length > 0) {
      let newData = data?.toString();
      let posScanConfig = tillData.posScanConfigs.filter((item) => item.formula !== null && item.scanTrxType === "PR" && item.barcodeLength === newData.length);
      const posConfigItem = posScanConfig?.find((item) => newData.startsWith(item.startsWith));
      if (posConfigItem) {
        const formula = JSON.parse(posConfigItem.formula);
        const productValue = newData.substr(Number(formula.property["product.value"]?.position - 1), Number(formula.property["product.value"]?.length));
        params.searchKey = productValue;
        bomQty = newData.substr(Number(formula.property["cartline.qty"]?.position - 1), Number(formula.property["cartline.qty"]?.length));
      }
    }
    const stringifiedFields = JSON.stringify(params);
    const updatedStrings = stringifiedFields.replace(/\\"/g, '\\"');
    const stringRequest = JSON.stringify(updatedStrings);
    const searchedProductQuery = { query: `mutation { executeAPIBuilder(apiBuilderId: "6746bb4a9c05362ba20c644f", params: ${stringRequest})}` };
    const serachedProductResponse = await getSearchedProducts(searchedProductQuery);
    if (serachedProductResponse.status === 200) {
      const searchedProductData = JSON.parse(serachedProductResponse.data.data.executeAPIBuilder);
      if (searchedProductData.length > 1) {
        setSearchedProductsFlag(true);
        setSearchedProductsDrawerData(searchedProductData);
      } else if (searchedProductData.length === 0) {
        notFoundModal();
      } else {
        let flag = false;
        let inputQty = 1;
        setQuantityInputValue((prev) => (inputQty = prev));
        let modifiedSearchedProductData = await Promise.all(
          searchedProductData?.map(async (product) => {
            let uomData = [];
            if (product.cs_uom_id) {
              await db.productUom
                .where("csUomId")
                .equals(product.cs_uom_id)
                .toArray()
                .then((uom) => {
                  if (uom.length > 0) uomData = uom;
                });
            }
            if (uomData[0]?.decimal === "Y") flag = true;
            return {
              ...product,
              ediCode: uomData[0]?.ediCode,
              uomData,
              weight: bomQty ? parseFloat(parseInt(bomQty) / 1000) : flag ? 0 : 1,
              totalWeight: bomQty ? parseFloat(parseInt(bomQty * inputQty) / 1000) : flag ? 0 : 1 * inputQty,
              total: product?.sale_price !== null ? (product?.sale_price * inputQty * bomQty ? parseFloat(parseInt(bomQty) / 1000) : 1 * product?.sale_price * inputQty) : 0,
              sale_price: product?.sale_price !== null ? parseFloat(product?.sale_price) : 0,
            };
          })
        );
        setTempSelectProduct(modifiedSearchedProductData);
        setSearchedProductsData((prevData) => {
          let index = prevData?.findIndex((item) => item.m_product_id === modifiedSearchedProductData[0].m_product_id);
          const matchedProduct = prevData?.filter((item) => {
            if (modifiedSearchedProductData.length > 0 && item.m_product_id === modifiedSearchedProductData[0].m_product_id) {
              return { ...item, totalWeight: (item.totalWeight || 0) + (modifiedSearchedProductData[0].totalWeight || 0) };
            }
          });
          let concatData = prevData;
          if (index !== -1) {
            modifiedSearchedProductData[0].weight = flag ? prevData[index].weight || 0 : (prevData[index].weight || 0) + (modifiedSearchedProductData[0].weight || 0);
            modifiedSearchedProductData[0].totalWeight = flag
              ? prevData[index].totalWeight || 0
              : (prevData[index].totalWeight || 0) + (modifiedSearchedProductData[0].totalWeight || 0);
            prevData[index] = modifiedSearchedProductData[0];
          } else {
            concatData = [...prevData, ...modifiedSearchedProductData];
          }
          const modifiedProductData = concatData?.map((product, index) => ({ ...product, key: index, stock: product?.stock !== null ? product?.stock : 0 }));
          const stockFlag = checkStockData(matchedProduct.length > 0 ? matchedProduct : concatData);
          if (stockFlag) {
            setIsVisible(true);
            return prevData;
          } else {
            let finalData = modifiedWithProperPrecision(modifiedProductData, pricePrecision);
            if (!flag) {
              savingSearchedProductsData(finalData);
              updateCartTotals(finalData);
            } else {
              setIsWeightPopupVisible(true);
            }
            return flag ? prevData : finalData;
          }
        });
        setSearchedProduct("");
        setSelectedRowKey(null);
        setShowInputFields(false);
      }
    }
  };

  const barCodeChooseProduct = async (data) => {
    const params = { limit: "50", offset: "0", searchKey: data, cs_bunit_id: csBunitId };
    const paramsForStock = { searchKey: data, bunitId: csBunitId };
    const barCodeConfig = posConfig?.showTareWeight === "Y" ? { id: "67b8539ef34cb07cda3d57b4", paramData: paramsForStock } : { id: "66f150288e3f1f618d89f45e", paramData: params };
    const chooseProductQuery = {
      query: `mutation ExecuteAPIBuilder($apiBuilderId: String, $params: String) { executeAPIBuilder(apiBuilderId: $apiBuilderId, params: $params)}`,
      variables: { apiBuilderId: barCodeConfig.id, params: JSON.stringify(barCodeConfig.paramData) },
    };
    const chooseProductResponse = await getChooseProduct(chooseProductQuery);
    if (chooseProductResponse.status === 200) {
      let chooseProductData = JSON.parse(chooseProductResponse?.data?.data?.executeAPIBuilder);
      if (chooseProductData.length === 1) {
        saveDateAndSalePrice(chooseProductData[0]);
        let uomData = [];
        if (chooseProductData[0].cs_uom_id) {
          await db.productUom
            .where("csUomId")
            .equals(chooseProductData[0].cs_uom_id)
            .toArray()
            .then((uom) => {
              if (uom.length > 0) uomData = uom;
            });
        }
        chooseProductData[0].uomData = uomData;
        chooseProductData[0].ediCode = uomData[0]?.ediCode;
        if (chooseProductData[0]?.productmanufacturing?.[0]?.standard_price === "Y") {
          savingPricingMethod("Standard Price");
          setPricingMethod("Standard Price");
          setSelectedProduct(chooseProductData[0]);
        }
        await addBOMLines(chooseProductData[0]);
        savingSelectedProduct(chooseProductData[0]);
        const currentDate = new Date();
        const today = currentDate.getDate().toString().padStart(2, "0") + "/" + (currentDate.getMonth() + 1).toString().padStart(2, "0") + "/" + currentDate.getFullYear();
        const date = getExpiryDate(today, chooseProductData[0]?.shelf_life || 0);
        setExpiryDate(date);
        savingExpiryDate(date);
        setTareWeight(chooseProductData[0]?.tareweight || 0);
        savingTareWeight(chooseProductData[0]?.tareweight || 0);
        setSelectedProduct(chooseProductData[0]);
        return false;
      } else if (chooseProductData.length === 0) {
        notFoundModal();
        let modifiedArr = await Promise.all(chooseProductData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData(modifiedArr);
        return true;
      } else {
        let modifiedArr = await Promise.all(chooseProductData?.map((product) => updateChooseProductWithUOMData(product)));
        setChooseProductsData(modifiedArr);
        return true;
      }
    }
  };

  const onBarcodeInput = (data, flag) => {
    setChooseProduct((prevData) => {
      if (prevData === true) {
        barCodeChooseProduct(data).then((result) => setChooseProduct(result));
      } else {
        setDisableSearch((prev) => {
          if (prev === false) fetchSearchedProductData(data, csBunitId);
          return prev;
        });
      }
      return prevData;
    });
  };

  const handleAddSearchedProduct = (product) => async () => {
    product.weight = 1;
    let inputQty = 1;
    setQuantityInputValue((prev) => (inputQty = prev));
    product.totalWeight = 1 * inputQty;
    product.sale_price = product.sale_price || 0;
    product.total = product.sale_price * inputQty;
    product.stock = product.stock || 0;
    const modifiedArr = await Promise.all([...searchedProductsData, product]?.map((product) => updateProductWithUOMData(product)));
    const stockFlag = checkStockData(modifiedArr);
    if (stockFlag) setIsVisible(true);
    else await addOrUpdateProducts(modifiedArr?.map((p, index) => ({ ...p, key: index })));
    setSearchedProductsFlag(false);
  };

  const handleDeleteSearchedProduct = (record) => {
    let updatedData = searchedProductsData.filter((product) => product.key !== record.key)?.map((product, index) => ({ ...product, key: index }));
    updateCartTotals(updatedData);
    setSearchedProductsData(updatedData);
    savingSearchedProductsData(updatedData);
    setSelectedRowKey(null);
    setShowInputFields(false);
  };

  const handleShift = () => {
    layoutName === "default" ? setLayoutName("shift") : setLayoutName("default");
  };

  const handleOtherChanges = async () => {
    if (otherChargesData.length === 0) {
      const otherChangesQuery = { query: `mutation { executeAPIBuilder(apiBuilderId:"66f408228e3f1f618d89f88b", params: "{}")}` };
      const otherChangesResponse = await getOtherCharges(otherChangesQuery);
      if (otherChangesResponse.status === 200) {
        const otherChargesData = JSON.parse(otherChangesResponse?.data?.data?.executeAPIBuilder);
        const updatedOtherChangesData = otherChargesData?.map((item) => ({ ...item, weight: "" }));
        setOtherChargesData(updatedOtherChangesData);
        setOtherCharges(true);
      }
    } else {
      setOtherCharges(true);
    }
  };

  const handleGetWeight = async () => {
    Axios.post(`${printerURL}getWeight`, {}, { headers: { "Content-Type": "application/xml; charset=UTF-8", Accept: "application/xml" } })
      .then((response) => {
        if (response.status === 200) {
          const weightWithEdicode = response.data.Weight;
          if (weightWithEdicode) {
            const weight = weightWithEdicode.split(" ");
            const finalValue = weight[0] === "" ? 0 : parseFloat(weight[0]);
            updateQuantityAndProducts(finalValue);
            const modifiedProductData = searchedProductsData?.map((product) => ({
              ...product,
              totalWeight: product.weight * finalValue,
              total: product.weight * finalValue * product.sale_price,
            }));
            let stockFlag = checkStockData(modifiedProductData);
            if (!stockFlag) {
              savingTotalWeight(finalValue);
              setTotalWeight(finalValue);
            }
          }
        }
      })
      .catch((response) => {
        message.error(`${messages.info.weight_failed}`);
      });
  };

  const handleCancel = () => {
    setTempRemarks("");
    setChooseProduct(false);
    setSelectedQuantity(0);
    setSelectedKey(null);
    setSelectedProduct(null);
    setTareWeight(0);
    setTotalWeight(0);
    setSelectedProductSalePrice(0);
    setChooseProductsData([]);
    setFormattedDate("");
    setSearchedProduct("");
    setChooseSearchedProduct("");
    setSearchedProductsData([]);
    setSearchedProductsFlag(false);
    setSearchedProductsDrawerData([]);
    setSubtotal(0);
    setTotalItems(0);
    setTotalQty(0);
    setTotalSalePrice(0);
    setRemarks("");
    setOtherChargesData([]);
    setTempOtherChargesData([]);
    setQuantityInputValue(posConfig?.showTareWeight === "Y" ? 0 : 1);
    setKeyBoardInputValue(1);
    setTempQuantityInputValue(1);
    setSelectedQuantity(0);
    setSelectedTotalQuantity(0);
    setPricingMethod("");
    setReverseOrderData([]);
    setDisableSearch(false);
    setExpiryDate("");
    setCopies(1);
    removeLocalStorageData();
  };

  const processBOMOrder = async () => {
    let inputProducts = [];
    searchedProductsData?.map((product) => {
      const totalWeight = product.totalWeight;
      const total = product.total.toFixed(pricePrecision);
      inputProducts.push({
        mProductId: product.m_product_id,
        csUomId: product.cs_uom_id,
        actualqty: hasReverseOrderData ? -parseFloat(totalWeight) : parseFloat(totalWeight),
        salePrice: hasReverseOrderData ? -product.sale_price : product.sale_price,
        totalPrice: hasReverseOrderData ? -parseFloat(total) : parseFloat(total),
      });
    });
    let indirectCost = [];
    otherChargesData?.map((charge) => {
      if (charge.weight !== "") {
        indirectCost.push({
          prIndirectcostId: charge.pr_indirectcost_id,
          value: hasReverseOrderData ? -parseInt(charge.weight) : parseInt(charge.weight),
          cost: hasReverseOrderData ? -parseInt(charge.weight) : parseInt(charge.weight),
        });
      }
    });

    const proceedQuery = {
      query: `mutation{
      upsertBOMProduction(order : {
              csBunitId: "${csBunitId}",
              date: "${formattedDate.split("/").reverse().join("-")}",
              mProductId: "${selectedProduct?.m_product_id}",
              qtyproduction: ${hasReverseOrderData ? "-" : ""}${quantityInputValue},
              description: "${remarks}"
              ${posConfig?.showSalePriceUnit === "Y" ? `salePrice: ${hasReverseOrderData ? "-" : ""}${parseFloat((totalSalePrice + totalWeights).toFixed(pricePrecision))}` : ""}
              ${posConfig?.showTotalPrice === "Y" ? `totalPrice: ${hasReverseOrderData ? "-" : ""}${parseFloat((totalSalePrice * quantityInputValue + totalWeights * quantityInputValue).toFixed(pricePrecision))}` : ""}
              inputProducts: ${JSON.stringify(inputProducts).replace(/"(\w+)":/g, "$1:")}
              indirectCost: ${JSON.stringify(indirectCost).replace(/"(\w+)":/g, "$1:")}
              reverse: ${reverseOrderData?.length > 0 ? `"Y"` : `"N"`}
              ${hasReverseOrderData ? `mBatchId: ${reverseOrderData[0]?.mBatchId ? `"${reverseOrderData[0]?.mBatchId}"` : reverseOrderData[0]?.mBatchId}` : ""}
              ${hasReverseOrderData ? `reverseOrderId: "${reverseOrderData[0]?.prOrderId}"` : ""}
          }){
          status
          message
          recordId
          upc
          batchNo
      }
  }`,
    };
    try {
      const proceedResponse = await upsertBOMProduction(proceedQuery);
      if (proceedResponse.status === 200) {
        const bomStatus = proceedResponse?.data?.data?.upsertBOMProduction?.status;
        const bomMessage = proceedResponse?.data?.data?.upsertBOMProduction?.message;
        if (bomStatus === "200") {
          const upc = proceedResponse?.data?.data?.upsertBOMProduction?.upc;
          const batchNo = proceedResponse?.data?.data?.upsertBOMProduction?.batchNo;
          if (reverseOrderData?.length === 0) {
            const json =
              posConfig?.showTareWeight === "Y"
                ? `{
                "elements": [
                  { "type": "barcode", "content": "${upc}", "width": 1, "height": 68, "x": 60, "y": 16 },
                  { "type": "text", "content": "${upc}", "align": "center", "style": ["normal", "bold"] },
                  { "type": "box", "x": 25, "y": 115, "width": 260, "thickness": 2, "text": [
                    { "content": "${selectedProduct?.value.toString().padStart(6, "0").slice(0, 6)}/${selectedProduct?.ediCode}/${selectedProduct?.name ? selectedProduct?.name?.slice(0, 18) : selectedProduct?.product?.slice(0, 18)}", "align": "center", "style": ["normal"], "offsetY": 10 },
                    { "content": "Weight: ${quantityInputValue} ${selectedProduct?.ediCode}", "align": "center", "style": ["normal"] },
                    { "content": "MFG:${formattedDate}  EXP:${expiryDate}", "align": "center", "style": ["normal"] }
                  ]}
                ],
                "settings": { "copies": ${copies}, "labelWidth": 305, "labelHeight": 406 }
              }`
                : `{
          "elements": [
            { "type": "barcode", "content": "${upc}", "width": 1, "height": 68, "x": 60, "y": 16 },
            { "type": "text", "content": "${upc}", "align": "center", "style": ["normal", "bold"] },
            { "type": "box", "x": 25, "y": 115, "width": 260, "thickness": 2, "text": [
              { "content": "${selectedProduct?.value.toString().padStart(6, "0").slice(0, 6)}/${selectedProduct?.ediCode}/${selectedProduct?.name ? selectedProduct?.name?.slice(0, 18) : selectedProduct?.product?.slice(0, 18)}", "align": "center", "style": ["normal"], "offsetY": 10 }
            ]},
            { "type": "text", "content": "Price: ${roundTo(totalSalePrice + totalWeights + (tillData.tillAccess?.cwrTill?.calculateTax === "Y" ? (selectedProduct?.taxrate ? ((totalSalePrice + totalWeights) * selectedProduct?.taxrate) / 100 : 0) : 0), pricePrecision)}", "align": "center", "style": ["normal"] },
            { "type": "text", "content": ${batchNo ? `"${batchNo}"` : batchNo}, "align": "center", "style": ["normal"] }
          ],
          "settings": { "copies": ${quantityInputValue}, "labelWidth": 305, "labelHeight": 406 }
        }`;
            Axios.post(`${printerURL}labelprinter`, json, { headers: { "Content-Type": "application/xml; charset=UTF-8", Accept: "application/xml" } })
              .then(() => console.debug("BOM Print success!"))
              .catch((response) => console.debug("BOM Print failed!", response));
          }
          handleCancel();
          message.success(`${proceedResponse?.data?.data?.upsertBOMProduction?.recordId} ${proceedResponse?.data?.data?.upsertBOMProduction?.message}`);
        } else if (bomStatus === "201") {
          Modal.error({ title: "Error", content: bomMessage, okText: "Okay", centered: true, maskClosable: true, okButtonProps: { style: { display: "inline-block" } } });
        }
      } else {
        message.error(`${messages.info.something_wrong_error}`);
      }
    } catch (error) {
      message.error(error);
      console.error("Proceed query failed", error);
    }
  };

  const handleProceed = () => {
    const modal = Modal.confirm({
      title: "Are you sure you want to proceed?",
      content: "This action will process the BOM order",
      okText: "Yes",
      cancelText: "No",
      cancelButtonProps: { disabled: false },
      okButtonProps: { style: { display: "inline-block" } },
      onOk: async () => {
        try {
          modal.update({ cancelButtonProps: { disabled: true } });
          await processBOMOrder();
          return true;
        } catch (err) {
          modal.update({ cancelButtonProps: { disabled: false } });
          return Promise.reject(err);
        }
      },
    });
  };

  const confirmUnlink = () => {
    Modal.confirm({
      title: "Confirm Unlink",
      content: "Are you sure you want to unlink the till?",
      okText: "Yes",
      cancelText: "Cancel",
      okButtonProps: { style: { display: "inline-block" } },
      onOk: async () => {
        await tillUnlink();
      },
      onCancel: () => console.debug("Unlink operation cancelled"),
    });
  };

  const tillUnlink = async () => {
    try {
      let id = tillData.tillAccess.cwrTill.cwrTillID;
      const response = await unlinkTill(id);
      if (response.data.data.unlinkTill.status === "200") {
        localStorage.clear();
        await Promise.all(
          db.tables?.map(async (table) => {
            if (table.name !== "") await table.clear();
          })
        );
      }
      const cookiesToDelete = document.cookie.split(";").map((cookie) => cookie.trim());
      for (let i = 0; i < cookiesToDelete.length; i++) {
        const cookie = cookiesToDelete[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        const domain = "domain=" + domainURL;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure=true; SameSite=Strict;" + domain;
      }
      useSessionStore.getState().setUnlink(false);
      window.location.assign(`${redirectUrl}sign-in`);
    } catch (error) {
      console.error("Error in unlinkTillAPI:", error);
    }
  };

  const handleLogin = () => {
    const cookiesToDelete = document.cookie.split(";")?.map((cookie) => cookie.trim());
    for (let i = 0; i < cookiesToDelete.length; i++) {
      const cookie = cookiesToDelete[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      const domain = "domain=" + domainURL;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure=true; SameSite=Strict;" + domain;
    }
    useSessionStore.getState().setLocked(isLocked);
    // BUG-1834: no trailing slash before `?` — react-router v5 `exact path="/sign-in"`
    // does NOT match `/sign-in/`, which left the page spinning forever after lock.
    window.location.assign(buildLockedSignInUrl(redirectUrl, { username, email }));
  };

  return {
    // Cart helpers
    modifiedData,
    updateCartTotals,
    checkStockData,
    updateQuantityAndProducts,
    addOrUpdateProducts,
    updateProductWithUOMData,
    updateChooseProductWithUOMData,
    // Edit handlers
    handleInputSubmit,
    handleEditStart,
    // Keypad handlers
    handleAmount,
    handleTotalAmount,
    handleAmountQty,
    handleEnter,
    handleTotalEnter,
    handleEnterQty,
    incrementQuantity,
    decrementQuantity,
    incrementTotalQuantity,
    decrementTotalQuantity,
    incrementQuantity1,
    decrementQuantity1,
    // Product selection
    handleProductSelect,
    handleChooseProductSelect,
    handleChooseProduct,
    handleScroll,
    // Stock/Order
    handleStockScroll,
    handleSearchedStock,
    handleOrderScroll,
    handleSearchedOrder,
    updateChooseProductsData,
    // Search/barcode
    fetchSearchedProductData,
    onBarcodeInput,
    handleAddSearchedProduct,
    handleDeleteSearchedProduct,
    // Other
    handleShift,
    handleOtherChanges,
    handleGetWeight,
    handleCancel,
    handleProceed,
    confirmUnlink,
    handleLogin,
  };
};

export default useProductionHandlers;
